import config from "../config/AdjustWeb";

let sdkPromise;
let sdkChannel;
const rechargeInFlight = new Map();

const readPersist = () => {
  try {
    return JSON.parse(localStorage.getItem("persist") || "{}") || {};
  } catch (error) {
    return {};
  }
};

export const GetAdjustWebChannel = () => {
  if (typeof window === "undefined") return "";
  if (window.JSBridge || window.JSInterface || window.SuWin8Bridge ||
      /GameApp|SKGames|AndroidApp/.test(window.navigator.userAgent) || localStorage.getItem("fromApp")) {
    return "";
  }
  const queryChannel = new URLSearchParams(window.location.search).get("c");
  const pathChannel = window.location.pathname.startsWith("/c/") ? window.location.pathname.substring(3) : "";
  const channel = queryChannel || pathChannel || readPersist().channel;
  return Object.prototype.hasOwnProperty.call(config.channels, channel) ? channel : "";
};

export const IsAdjustWebChannel = () => Boolean(GetAdjustWebChannel());

const saveAttribution = (sdk, attribution, channel) => {
  if (GetAdjustWebChannel() !== channel || !attribution || !attribution.adid) return;
  const previous = localStorage.getItem("attribution");
  // 保留其他渠道已有归因；允许本次 Web 归因回调补全推广信息。
  if (previous && !(previous === "adjust" &&
      localStorage.getItem(`adjust_${channel}_adid`) === localStorage.getItem("attribution_id"))) return;
  const source = { ...attribution, channel, platform: "web" };
  const webUUID = sdk.getWebUUID();
  if (webUUID) source.web_uuid = webUUID;
  localStorage.setItem("attribution", "adjust");
  localStorage.setItem("attribution_id", attribution.adid);
  localStorage.setItem("ad_source", JSON.stringify(source));
  localStorage.setItem(`adjust_${channel}_adid`, attribution.adid);
};

export const InitAdjustWeb = () => {
  const channel = GetAdjustWebChannel();
  if (!channel) return Promise.resolve(null);
  // SDK 在当前页面只初始化一次，避免渠道切换时复用另一个 tracker。
  if (sdkPromise && sdkChannel !== channel) return Promise.resolve(null);
  if (!sdkPromise) {
    sdkChannel = channel;
    sdkPromise = import("@adjustcom/adjust-web-sdk").then((module) => {
      if (GetAdjustWebChannel() !== channel) {
        sdkPromise = null;
        return null;
      }
      const sdk = module.default || module;
      const env = process.env.PREACT_APP_ENV_CONFIG || {};
      const sandbox = process.env.NODE_ENV !== "production" || env.BUILD_FOR === "TEST" || env.BUILD_FOR === "WIFI_TEST";
      const options = {
        appToken: config.appToken,
        environment: sandbox ? "sandbox" : "production",
        logLevel: sandbox ? "verbose" : "error",
        namespace: channel,
        attributionCallback: (event, attribution) => saveAttribution(sdk, attribution, channel),
      };
      options.defaultTracker = config.channels[channel];
      sdk.initSdk(options);
      const user = readPersist().user_info;
      if (user && user.rid) sdk.addGlobalPartnerParameters([{ key: "user_id", value: String(user.rid) }]);
      sdk.waitForAttribution().then((attribution) => saveAttribution(sdk, attribution, channel)).catch(reportError);
      sdk.waitForWebUUID().then(() => saveAttribution(sdk, sdk.getAttribution(), channel)).catch(reportError);
      return sdk;
    }).catch((error) => {
      sdkPromise = null;
      reportError(error);
      return null;
    });
  }
  return sdkPromise;
};

// SDK errors can contain request parameters. Keep diagnostics free of payloads.
const reportError = () => console.warn("[Adjust H5]", { reason: "sdk_error" });
const textValue = (value) => value == null ? "" : String(value).trim();

const track = (sdk, eventToken, data, requireRevenue, suffix = "") => {
  const revenue = Number(data.revenue == null ? data.value : data.revenue);
  const currency = textValue(data.currency).toUpperCase();
  const hasRevenue = Number.isFinite(revenue) && revenue > 0 && /^[A-Z]{3}$/.test(currency);
  if (requireRevenue && !hasRevenue) return Promise.resolve();
  const event = { eventToken, partnerParams: [] };
  const transactionId = textValue(data.transactionId || data.transaction_id);
  const userId = textValue(data.rid || data.user_id);
  if (transactionId) {
    event.deduplicationId = transactionId + suffix;
    event.partnerParams.push({ key: "transaction_id", value: transactionId });
  }
  if (userId) event.partnerParams.push({ key: "user_id", value: userId });
  if ((requireRevenue || suffix) && hasRevenue) {
    event.revenue = revenue;
    event.currency = currency;
  }
  return sdk.trackEvent(event);
};

export const SendAdjustWebEvent = (event, data = {}) => {
  const name = textValue(event).toLowerCase();
  // PP 的注册转化仅计正式账号，游客注册不能占用唯一注册事件。
  if (name === "register2" && GetAdjustWebChannel() === "pp") return Promise.resolve();
  if (!["register", "register2", "recharge", "purchase", "first_purchase", "firstpurchase"].includes(name)) {
    return Promise.resolve();
  }
  return InitAdjustWeb().then((sdk) => {
    if (!sdk || GetAdjustWebChannel() !== sdkChannel) return;
    if (name === "register" || name === "register2") return track(sdk, config.registerToken, data, false);
    if (name === "first_purchase" || name === "firstpurchase") {
      return track(sdk, config.firstPurchaseToken, data, false, "_first_purchase");
    }
    const events = [track(sdk, config.purchaseToken, data, true)];
    if (data.isFirstPurchase === true || data.is_first_purchase === true) {
      events.push(track(sdk, config.firstPurchaseToken, data, true, "_first_purchase"));
    }
    return Promise.all(events);
  }).catch(reportError);
};

const rechargeStatus = (status, reason) => reason ? { status, reason } : { status };

const rechargePayloadReason = (data) => {
  if (!textValue(data.transactionId || data.transaction_id)) return "missing_transaction_id";
  const revenue = Number(data.revenue == null ? data.value : data.revenue);
  if (!Number.isFinite(revenue) || revenue <= 0) return "invalid_revenue";
  if (!/^[A-Z]{3}$/.test(textValue(data.currency).toUpperCase())) return "invalid_currency";
  return "";
};

const canDispatchRecharge = (canDispatch) => {
  try {
    return typeof canDispatch !== "function" || canDispatch() !== false;
  } catch (error) {
    return false;
  }
};

const trackRechargeEvent = async (sdk, eventToken, data, suffix, canDispatch) => {
  if (!canDispatchRecharge(canDispatch)) return rechargeStatus("retry", "account_changed");
  if (!textValue(eventToken)) return rechargeStatus("blocked", "missing_event_token");
  try {
    // The SDK may resolve once its durable queue accepts the event. This is not
    // an Adjust server acknowledgement or a PropellerAds conversion receipt.
    const pending = track(sdk, eventToken, data, true, suffix);
    if (!pending || typeof pending.then !== "function") {
      return rechargeStatus("blocked", "sdk_result_unavailable");
    }
    const result = await pending;
    if (result && (result.status === "error" || (result.response && result.response.error))) {
      return rechargeStatus("blocked", "sdk_server_rejected");
    }
    return rechargeStatus("sdk_queued");
  } catch (error) {
    // The SDK reserves deduplication IDs before writing its event queue. A
    // duplicate rejection alone therefore cannot prove the event was queued.
    const message = typeof error === "string" ? error : error && error.message;
    const duplicate = typeof message === "string" && /same deduplication id|previously tracked/i.test(message);
    return rechargeStatus(duplicate ? "blocked" : "retry", duplicate ? "sdk_duplicate_unverified" : "sdk_rejected");
  }
};

const queueRechargeEvent = (sdk, eventToken, data, suffix, canDispatch) => {
  const key = `${sdkChannel}:${eventToken}:${textValue(data.transactionId || data.transaction_id)}${suffix}`;
  if (rechargeInFlight.has(key)) return rechargeInFlight.get(key);
  const pending = trackRechargeEvent(sdk, eventToken, data, suffix, canDispatch);
  rechargeInFlight.set(key, pending);
  const clear = () => rechargeInFlight.delete(key);
  pending.then(clear, clear);
  return pending;
};

export const SendAdjustWebRecharge = async (data = {}, {
  purchaseQueued = false,
  firstPurchaseQueued = false,
  canDispatch,
} = {}) => {
  data = data || {};
  const firstRequested = data.isFirstPurchase === true || data.is_first_purchase === true;
  const result = {
    transport: "none",
    purchase: rechargeStatus(purchaseQueued ? "sdk_queued" : "not_applicable"),
    firstPurchase: rechargeStatus(firstPurchaseQueued ? "sdk_queued" : firstRequested ? "not_applicable" : "not_requested"),
  };
  let channel;
  try {
    channel = GetAdjustWebChannel();
  } catch (error) {
    if (!purchaseQueued) result.purchase = rechargeStatus("retry", "channel_unavailable");
    if (firstRequested && !firstPurchaseQueued) result.firstPurchase = rechargeStatus("retry", "channel_unavailable");
    return result;
  }
  if (!channel) return result;
  result.transport = "web_sdk";
  if (purchaseQueued && (!firstRequested || firstPurchaseQueued)) return result;

  const invalidReason = rechargePayloadReason(data);
  if (invalidReason) {
    if (!purchaseQueued) result.purchase = rechargeStatus("blocked", invalidReason);
    if (firstRequested && !firstPurchaseQueued) result.firstPurchase = rechargeStatus("blocked", invalidReason);
    return result;
  }

  let sdk;
  try {
    sdk = await InitAdjustWeb();
    if (!canDispatchRecharge(canDispatch)) {
      if (!purchaseQueued) result.purchase = rechargeStatus("retry", "account_changed");
      if (firstRequested && !firstPurchaseQueued) result.firstPurchase = rechargeStatus("retry", "account_changed");
      return result;
    }
    if (!sdk || GetAdjustWebChannel() !== channel || sdkChannel !== channel) {
      if (!purchaseQueued) result.purchase = rechargeStatus("retry", "sdk_unavailable");
      if (firstRequested && !firstPurchaseQueued) result.firstPurchase = rechargeStatus("retry", "sdk_unavailable");
      return result;
    }
  } catch (error) {
    if (!purchaseQueued) result.purchase = rechargeStatus("retry", "sdk_unavailable");
    if (firstRequested && !firstPurchaseQueued) result.firstPurchase = rechargeStatus("retry", "sdk_unavailable");
    return result;
  }

  // Each event owns its result: a failed first purchase must not cause an
  // already queued purchase to be submitted again on a later attempt.
  const tasks = [];
  if (!purchaseQueued) {
    result.purchase = rechargeStatus("retry", "sdk_rejected");
    tasks.push(queueRechargeEvent(sdk, config.purchaseToken, data, "", canDispatch).then((status) => { result.purchase = status; }));
  }
  if (firstRequested && !firstPurchaseQueued) {
    result.firstPurchase = rechargeStatus("retry", "sdk_rejected");
    tasks.push(queueRechargeEvent(sdk, config.firstPurchaseToken, data, "_first_purchase", canDispatch).then((status) => { result.firstPurchase = status; }));
  }
  await Promise.allSettled(tasks);
  return result;
};

export const BindAdjustWebUser = (rid) => {
  if (!textValue(rid)) return Promise.resolve();
  return InitAdjustWeb().then((sdk) => {
    if (sdk && GetAdjustWebChannel() === sdkChannel) sdk.addGlobalPartnerParameters([{ key: "user_id", value: textValue(rid) }]);
  }).catch(reportError);
};
